import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import DonateModal from './DonateModal';
import Icon from '../Shared/Icons';
import logo from '../../logo.png';

// ── Fixed pricing ────────────────────────────────────────────────────────
const STUDENT_PLANS = [
  { id: 'monthly', label: 'Monthly', amount: '$19', period: '/month', description: 'Academic support · Stationery · Sports & extracurricular · School uniform' },
  { id: 'annual', label: 'Annual', amount: '$228', period: '/year', badge: 'Best Value', description: 'Full year of support — equivalent to $19/month' },
];

const PARENT_CAPITAL_OPTIONS = [
  { id: 'c1', usd: 19, tzs: '50,000', label: 'Starter', description: 'Small stock or supplies to begin a micro-business' },
  { id: 'c2', usd: 38, tzs: '100,000', label: 'Growth', description: 'Enough to stock a market stall or kiosk for a month' },
  { id: 'c3', usd: 76, tzs: '200,000', label: 'Expansion', description: 'Expand an existing business and hire help' },
  { id: 'c4', usd: 190, tzs: '500,000', label: 'Full Capital', description: 'Full startup capital for a sustainable family business' },
];

// ── Child-safe avatar — no photos, first names only ─────────────────────
function SafeAvatar({ gender, size = 46 }) {
  const isFemale = (gender || '').toLowerCase() === 'female';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: isFemale
        ? 'linear-gradient(135deg, #F57C00, #FFA726)'
        : 'linear-gradient(135deg, #14452F, #1F6B45)',
    }}>
      <Icon name="user" size={size * 0.5} color="#fff" strokeWidth={2} />
    </div>
  );
}

export default function DonorHome() {
  const [stats, setStats] = useState({ students: 0, parents: 0 });
  const [stories, setStories] = useState([]);
  const [donateFor, setDonateFor] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDonate, setShowDonate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const safe = async (name) => {
          try { return (await getDocs(collection(db, name))).docs; }
          catch { return []; }
        };
        const [studentDocs, parentDocs] = await Promise.all([
          safe('students'), safe('parents'),
        ]);
        setStats({
          students: studentDocs.length,
          parents: parentDocs.length,
        });
        // Family stories — FIRST NAME ONLY, no photos, gender avatar
        setStories(
          parentDocs
            .map(d => d.data())
            .filter(p => p.familyStory?.before || p.familyStory?.journey || p.familyStory?.now)
            .slice(0, 12)
            .map(p => ({
              firstName: p.firstName || 'Family',
              gender: p.gender,
              childrenEnrolled: p.childrenEnrolled,
              story: p.familyStory,
            }))
        );
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const TABS = [
    { id: 'overview', icon: 'home', label: 'Overview' },
    { id: 'support', icon: 'heart', label: 'Support Someone' },
    { id: 'stories', icon: 'book', label: 'Family Stories' },
  ];

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div style={styles.brandRow}>
          <div style={styles.logo}>
            <img src={logo} alt="Iyanna Foundation" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
          <div>
            <div style={styles.brandName}>Iyanna Foundation</div>
            <div style={styles.brandSub}>Supporting children & families in Tanzania</div>
          </div>
        </div>
        <div style={styles.headerActions}>
          <Link to="/donor/ai-assistant" style={styles.aiLink}>
            <Icon name="ai" size={15} /> Ask our AI
          </Link>
          <Link to="/chess" style={styles.chessLink}>
            <Icon name="chess" size={15} /> Chess App
          </Link>
          <button style={styles.donateBtn} onClick={() => setShowDonate(true)}>
            <Icon name="heart" size={15} color="#fff" /> Donate
          </button>
        </div>
      </div>

      {/* AI ASSISTANT PROMPT CARD */}
      <div style={styles.aiPromptCard}>
        <div style={styles.aiPromptIcon}><Icon name="ai" size={22} color="#14452F" /></div>
        <div style={{ flex: 1 }}>
          <div style={styles.aiPromptTitle}>Curious about our progress?</div>
          <div style={styles.aiPromptText}>
            Ask our AI Assistant about students supported and the impact your donation makes.
          </div>
        </div>
        <Link to="/donor/ai-assistant" style={styles.aiPromptBtn}>Ask Now</Link>
      </div>

      {/* HERO */}
      <div style={styles.hero}>
        <div style={styles.heroEyebrow}>NEW INITIATIVE · ARUSHA</div>
        <div style={styles.heroTitle}>Supporting Children in Arusha</div>
        <div style={styles.heroText}>
          Our aim is to give every child access to quality education, life skills training, and
          protection — creating a safe, nurturing environment where they can grow with confidence
          and purpose. Through chess coaching, we help children build critical thinking, patience,
          and resilience — skills that carry far beyond the board.
        </div>
        <div style={styles.heroStats}>
          <div style={styles.heroStat}>
            <div style={styles.heroStatNum}>{loading ? '—' : stats.students}</div>
            <div style={styles.heroStatLabel}>Students</div>
          </div>
          <div style={styles.heroStatDivider} />
          <div style={styles.heroStat}>
            <div style={styles.heroStatNum}>{loading ? '—' : stats.parents}</div>
            <div style={styles.heroStatLabel}>Families</div>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div style={styles.tabRow}>
        {TABS.map(t => (
          <button
            key={t.id}
            style={{ ...styles.tabBtn, ...(activeTab === t.id ? styles.tabBtnActive : {}) }}
            onClick={() => setActiveTab(t.id)}
          >
            <Icon name={t.icon} size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {activeTab === 'overview' && (
        <div style={styles.content}>
          <div style={styles.sectionLabel}>What We Do</div>
          <div style={styles.whatGrid}>
            {[
              { icon: 'students', title: 'Education Support', desc: 'School fees, uniforms, books, pens, bags and stationery for every enrolled student.' },
              { icon: 'chess', title: 'Chess & Life Skills', desc: 'Weekly sessions using chess to develop focus, patience, resilience and critical thinking.' },
              { icon: 'lock', title: 'Child Protection', desc: 'Safeguards and a strong focus on overall wellbeing, so no child is left behind.' },
              { icon: 'seedling', title: 'Microfinance', desc: 'Capital for parents to start businesses so families become financially independent.' },
              { icon: 'ai', title: 'Parent Program', desc: 'Reflective Approach sessions helping parents support children and manage finances.' },
              { icon: 'chart', title: 'Progress Tracking', desc: 'We track every child academically, socially, and at home.' },
            ].map((w, i) => (
              <div key={i} style={styles.whatCard}>
                <div style={styles.whatIcon}><Icon name={w.icon} size={22} color="#14452F" /></div>
                <div style={styles.whatTitle}>{w.title}</div>
                <div style={styles.whatDesc}>{w.desc}</div>
              </div>
            ))}
          </div>

          <div style={styles.noticeCard}>
            <Icon name="lock" size={16} color="#1565C0" />
            <div>
              <strong>Child safety first.</strong> To protect the children we serve, this page never
            shows children's photos or full names. Families are shown by first name only.
            </div>
          </div>
        </div>
      )}

      {/* ── SUPPORT ── */}
      {activeTab === 'support' && (
        <div style={styles.content}>
          <div style={styles.sectionLabel}>Sponsor a Student</div>
          <div style={styles.planGrid}>
            {STUDENT_PLANS.map(p => (
              <div key={p.id} style={styles.planCard}>
                {p.badge && <div style={styles.planBadge}>{p.badge}</div>}
                <div style={styles.planAmount}>{p.amount}<span style={styles.planPeriod}>{p.period}</span></div>
                <div style={styles.planLabel}>{p.label} Sponsorship</div>
                <div style={styles.planDesc}>{p.description}</div>
                <button style={styles.planBtn} onClick={() => setShowDonate(true)}>
                  <Icon name="heart" size={14} color="#fff" /> Sponsor Now
                </button>
              </div>
            ))}
          </div>

          <div style={styles.sectionLabel}>Empower a Parent's Business</div>
          <div style={styles.capitalGrid}>
            {PARENT_CAPITAL_OPTIONS.map(c => (
              <div key={c.id} style={styles.capitalCard}>
                <div style={styles.capitalLabel}>{c.label}</div>
                <div style={styles.capitalAmount}>${c.usd}</div>
                <div style={styles.capitalTzs}>TSh {c.tzs}</div>
                <div style={styles.capitalDesc}>{c.description}</div>
                <button style={styles.capitalBtn} onClick={() => setShowDonate(true)}>Give Capital</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── STORIES ── */}
      {activeTab === 'stories' && (
        <div style={styles.content}>
          <div style={styles.sectionLabel}>Family Stories</div>
          {loading ? (
            <div style={styles.emptyText}>Loading stories…</div>
          ) : stories.length === 0 ? (
            <div style={styles.emptyText}>Family stories will appear here soon.</div>
          ) : (
            <div style={styles.storyGrid}>
              {stories.map((s, i) => (
                <div key={i} style={styles.storyCard}>
                  <div style={styles.storyHead}>
                    <SafeAvatar gender={s.gender} />
                    <div>
                      <div style={styles.storyName}>{s.firstName}</div>
                      <div style={styles.storyMeta}>
                        {Number(s.childrenEnrolled) > 0
                          ? `${s.childrenEnrolled} ${Number(s.childrenEnrolled) === 1 ? 'child' : 'children'} in the program`
                          : 'Program family'}
                      </div>
                    </div>
                  </div>
                  {s.story?.before && (
                    <div style={styles.storyBlock}>
                      <div style={styles.storyBlockLabel}>Before</div>
                      <div style={styles.storyBlockText}>{s.story.before}</div>
                    </div>
                  )}
                  {s.story?.journey && (
                    <div style={styles.storyBlock}>
                      <div style={styles.storyBlockLabel}>The Journey</div>
                      <div style={styles.storyBlockText}>{s.story.journey}</div>
                    </div>
                  )}
                  {s.story?.now && (
                    <div style={styles.storyBlock}>
                      <div style={styles.storyBlockLabel}>Today</div>
                      <div style={styles.storyBlockText}>{s.story.now}</div>
                    </div>
                  )}
                  <button
                    style={styles.storySupportBtn}
                    onClick={() => { setDonateFor(`${s.firstName}'s family`); setShowDonate(true); }}
                  >
                    <Icon name="heart" size={13} color="#fff" /> Support {s.firstName}'s Family
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FOOTER */}
      <div style={styles.footer}>
        <Link to="/login" style={styles.portalLink}>
          <Icon name="key" size={12} /> Admin/Staff Portal
        </Link>
      </div>

      {showDonate && <DonateModal onClose={() => { setShowDonate(false); setDonateFor(null); }} forWhom={donateFor} />}
    </div>
  );
}

const G = '#14452F';
const styles = {
  page: { minHeight: '100vh', background: '#F6F4EE', paddingBottom: '40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px', padding: '18px 22px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.06)' },
  brandRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { width: '46px', height: '46px', borderRadius: '50%', background: '#041F14', padding: '0px', overflow: 'hidden'},
  brandName: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '18px', fontWeight: '700', color: '#1C1C1E' },
  brandSub: { fontSize: '11.5px', color: '#6B7280' },
  headerActions: { display: 'flex', gap: '10px', alignItems: 'center' },
  chessLink: { display: 'inline-flex', alignItems: 'center', gap: '6px', color: G, textDecoration: 'none', fontSize: '13px', fontWeight: '600', border: `1.5px solid ${G}`, padding: '9px 16px', borderRadius: '10px' },
  aiLink: { display: 'inline-flex', alignItems: 'center', gap: '6px', color: G, textDecoration: 'none', fontSize: '13px', fontWeight: '600', border: `1.5px solid ${G}`, padding: '9px 16px', borderRadius: '10px' },
  aiPromptCard: { maxWidth: '980px', margin: '18px auto 0', paddingInline: '16px', display: 'flex', alignItems: 'center', gap: '14px', background: '#fff', border: '1px solid rgba(0,0,0,0.05)', borderRadius: '16px', padding: '16px 20px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  aiPromptIcon: { width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(20,69,47,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  aiPromptTitle: { fontSize: '13.5px', fontWeight: '700', color: '#1C1C1E' },
  aiPromptText: { fontSize: '12px', color: '#6B7280', marginTop: '2px' },
  aiPromptBtn: { flexShrink: 0, background: G, color: '#fff', textDecoration: 'none', padding: '10px 18px', borderRadius: '10px', fontSize: '12.5px', fontWeight: '700' },
  donateBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #F57C00, #FFA726)', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(245,124,0,0.3)' },
  hero: { maxWidth: '980px', margin: '24px auto 0', padding: '38px 32px', borderRadius: '22px', background: 'linear-gradient(135deg, #0B3524 0%, #14452F 55%, #1E4D33 100%)', color: '#fff', marginLeft: 'auto', marginRight: 'auto', boxShadow: '0 14px 40px rgba(13,51,16,0.25)', marginInline: 'max(16px, calc((100% - 980px) / 2))' },
  heroEyebrow: { fontSize: '11px', fontWeight: '700', letterSpacing: '1.4px', color: '#FFB74D', marginBottom: '10px' },
  heroTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: 'clamp(24px, 4vw, 34px)', fontWeight: '700', marginBottom: '12px', lineHeight: 1.2 },
  heroText: { fontSize: '14px', color: 'rgba(255,255,255,0.78)', lineHeight: 1.75, maxWidth: '640px', marginBottom: '24px' },
  heroStats: { display: 'flex', gap: '22px', alignItems: 'center', flexWrap: 'wrap' },
  heroStat: { textAlign: 'center' },
  heroStatNum: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '28px', fontWeight: '700', color: '#FFB74D' },
  heroStatLabel: { fontSize: '11.5px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' },
  heroStatDivider: { width: '1px', height: '34px', background: 'rgba(255,255,255,0.15)' },
  tabRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', maxWidth: '980px', margin: '20px auto 0', paddingInline: '16px' },
  tabBtn: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: '#fff', border: '1.5px solid rgba(0,0,0,0.08)', color: '#4B5563', padding: '10px 18px', borderRadius: '12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  tabBtnActive: { background: G, borderColor: G, color: '#fff' },
  content: { maxWidth: '980px', margin: '22px auto 0', paddingInline: '16px' },
  sectionLabel: { fontSize: '11.5px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#6B7280', margin: '18px 0 4px' },
  sectionHint: { fontSize: '12px', color: '#9CA3AF', marginBottom: '12px' },
  whatGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' },
  whatCard: { background: '#fff', borderRadius: '16px', padding: '20px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  whatIcon: { width: '44px', height: '44px', borderRadius: '12px', background: 'rgba(20,69,47,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' },
  whatTitle: { fontSize: '14.5px', fontWeight: '700', color: '#1C1C1E', marginBottom: '6px' },
  whatDesc: { fontSize: '12.5px', color: '#6B7280', lineHeight: 1.6 },
  noticeCard: { display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#E3F2FD', border: '1px solid #BBDEFB', color: '#1565C0', padding: '14px 16px', borderRadius: '12px', fontSize: '12.5px', lineHeight: 1.6, marginTop: '18px' },
  planGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '14px', marginBottom: '10px' },
  planCard: { position: 'relative', background: '#fff', borderRadius: '18px', padding: '24px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  planBadge: { position: 'absolute', top: '14px', right: '14px', background: 'rgba(245,124,0,0.12)', color: '#E65100', fontSize: '10.5px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px' },
  planAmount: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '32px', fontWeight: '700', color: G },
  planPeriod: { fontSize: '14px', color: '#9CA3AF', fontWeight: '400' },
  planLabel: { fontSize: '14px', fontWeight: '700', color: '#1C1C1E', marginTop: '4px' },
  planDesc: { fontSize: '12.5px', color: '#6B7280', lineHeight: 1.6, margin: '10px 0 16px' },
  planBtn: { display: 'inline-flex', alignItems: 'center', gap: '7px', width: '100%', justifyContent: 'center', background: `linear-gradient(135deg, ${G}, #1F6B45)`, color: '#fff', border: 'none', padding: '12px', borderRadius: '10px', fontSize: '13.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  capitalGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' },
  capitalCard: { background: '#fff', borderRadius: '16px', padding: '18px', border: '1px solid rgba(0,0,0,0.05)', textAlign: 'center' },
  capitalLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: '#F57C00' },
  capitalAmount: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '26px', fontWeight: '700', color: '#1C1C1E', marginTop: '6px' },
  capitalTzs: { fontSize: '11.5px', color: '#9CA3AF' },
  capitalDesc: { fontSize: '12px', color: '#6B7280', lineHeight: 1.55, margin: '10px 0 14px' },
  capitalBtn: { width: '100%', background: 'transparent', border: `1.5px solid ${G}`, color: G, padding: '10px', borderRadius: '10px', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  storyGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '14px' },
  storyCard: { background: '#fff', borderRadius: '18px', padding: '20px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  storyHead: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' },
  storyName: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '17px', fontWeight: '700', color: '#1C1C1E' },
  storyMeta: { fontSize: '11.5px', color: '#6B7280', marginTop: '2px' },
  storyBlock: { marginBottom: '12px' },
  storyBlockLabel: { fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.6px', textTransform: 'uppercase', color: '#F57C00', marginBottom: '4px' },
  storyBlockText: { fontSize: '12.5px', color: '#4B5563', lineHeight: 1.65 },
  emptyText: { color: '#9CA3AF', fontSize: '13px', padding: '20px 0' },
  personGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginBottom: '8px' },
  personCard: { background: '#fff', borderRadius: '14px', padding: '16px 12px', border: '1px solid rgba(0,0,0,0.05)', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  personName: { fontSize: '13px', fontWeight: '700', color: '#1C1C1E', marginTop: '10px' },
  personMeta: { fontSize: '10.5px', color: '#9CA3AF', marginTop: '2px', marginBottom: '10px' },
  personBtnRow: { display: 'flex', flexDirection: 'column', gap: '6px' },
  personBtn: { width: '100%', background: '#14452F', border: '1.5px solid #14452F', color: '#fff', padding: '7px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  personBtnAlt: { width: '100%', background: 'transparent', border: '1.5px solid #14452F', color: '#14452F', padding: '7px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  storySupportBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', width: '100%', justifyContent: 'center', marginTop: '6px', background: 'linear-gradient(135deg, #F57C00, #FFA726)', border: 'none', color: '#fff', padding: '10px', borderRadius: '9px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' },
  footer: { textAlign: 'center', marginTop: '36px' },
  portalLink: { display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#9CA3AF', fontSize: '11.5px', textDecoration: 'none' },
};
